"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Suspense,useDeferredValue, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  FilterX,
  Layers3,
  Loader2,
  PackageSearch,
  Search,
  SlidersHorizontal,
  Store,
} from "lucide-react";
import Image from "next/image";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  fetchWelcomeProducts,
  type WelcomeProductCatalogItem,
  type WelcomeProductsFilters,
  type WelcomeProductSort,
} from "@/services/welcomeService";

const PRODUCT_PAGE_SIZE = 20;
const numberFormatter = new Intl.NumberFormat("en-IN");
const EMPTY_FILTERS: WelcomeProductsFilters = {
  rootCategories: [],
  categories: [],
  suppliers: [],
};
const SORT_OPTIONS: { value: WelcomeProductSort; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "name_asc", label: "Name A-Z" },
  { value: "name_desc", label: "Name Z-A" },
];

const buildRequestKey = ({
  query,
  categoryId,
  sellerProfileId,
  verified,
  hasVariants,
  sort,
}: {
  query: string;
  categoryId: string;
  sellerProfileId: string;
  verified: boolean;
  hasVariants: boolean;
  sort: WelcomeProductSort;
}) =>
  JSON.stringify({
    query,
    categoryId,
    sellerProfileId,
    verified,
    hasVariants,
    sort,
  });

const formatCompactCount = (value: number) =>
  numberFormatter.format(Math.max(0, Number(value) || 0));

function ProductGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 min-[520px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, index) => (
        <div
          key={`product-skeleton-${index}`}
          className="card-surface overflow-hidden"
        >
          <div className="h-44 w-full animate-pulse bg-muted" />
          <div className="space-y-3 p-4">
            <div className="h-3 w-20 animate-pulse rounded bg-muted" />
            <div className="h-4 w-4/5 animate-pulse rounded bg-muted" />
            <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
            <div className="h-5 w-28 animate-pulse rounded bg-muted" />
            <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ProductCard({ item }: { item: WelcomeProductCatalogItem }) {
  const createdLabel = item.createdAt
    ? new Date(item.createdAt).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <Link
      href={`/welcome/products/${item.id}`}
      className="card-surface card-hover block overflow-hidden"
    >
      <div className="relative h-44 overflow-hidden bg-muted">
        <Image
          src={item.image}
          alt={item.name}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 25vw"
          className="object-cover transition-transform duration-300 hover:scale-[1.03]"
        />
        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {item.verified ? (
            <Badge className="gap-1 bg-white/95 text-slate-900 hover:bg-white">
              <CheckCircle2 className="h-3 w-3 text-emerald-600" />
              Verified supplier
            </Badge>
          ) : null}
          {item.hasVariants ? (
            <Badge
              variant="secondary"
              className="gap-1 bg-slate-900/85 text-white hover:bg-slate-900"
            >
              <Layers3 className="h-3 w-3" />
              Variants
            </Badge>
          ) : null}
        </div>
      </div>

      <div className="space-y-3 p-4">
        <div className="space-y-1">
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            {item.categoryDisplayName}
          </p>
          <h2 className="line-clamp-2 text-base font-semibold tracking-tight text-foreground">
            {item.name}
          </h2>
          {item.code ? (
            <p className="text-xs text-muted-foreground">Code: {item.code}</p>
          ) : null}
        </div>

        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-xl font-semibold text-primary">{item.price}</p>
            <p className="text-xs text-muted-foreground">MOQ {item.moq}</p>
          </div>
          <Badge variant="secondary" className="rounded-full">
            {formatCompactCount(item.skuCount)} SKUs
          </Badge>
        </div>

        <div className="rounded-lg border border-border/70 bg-muted/35 p-3 text-sm">
          <div className="flex items-center gap-2 text-foreground">
            <Store className="h-4 w-4 text-primary" />
            <span className="line-clamp-1 font-medium">{item.supplier}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {item.location || "India"}
          </p>
          {createdLabel ? (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Listed on {createdLabel}
            </p>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

function WelcomeProductsPageContent() {
  const searchParams = useSearchParams();
  const queryFromUrl = searchParams.get("q")?.trim() ?? "";
  const [searchInput, setSearchInput] = useState(queryFromUrl);
  const deferredQuery = useDeferredValue(searchInput.trim());
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedSupplierId, setSelectedSupplierId] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [variantsOnly, setVariantsOnly] = useState(false);
  const [sort, setSort] = useState<WelcomeProductSort>("newest");
  const [items, setItems] = useState<WelcomeProductCatalogItem[]>([]);
  const [filters, setFilters] = useState<WelcomeProductsFilters>(EMPTY_FILTERS);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const latestRequestKeyRef = useRef("");

  useEffect(() => {
    setSearchInput(queryFromUrl);
  }, [queryFromUrl]);

  useEffect(() => {
    let isActive = true;

    const requestKey = buildRequestKey({
      query: deferredQuery,
      categoryId: selectedCategoryId,
      sellerProfileId: selectedSupplierId,
      verified: verifiedOnly,
      hasVariants: variantsOnly,
      sort,
    });

    latestRequestKeyRef.current = requestKey;
    setIsInitialLoading(true);
    setIsLoadingMore(false);
    setError(null);
    setItems([]);
    setNextCursor(null);
    setHasMore(false);

    const loadProducts = async () => {
      try {
        const payload = await fetchWelcomeProducts({
          query: deferredQuery,
          categoryId: selectedCategoryId || null,
          sellerProfileId: selectedSupplierId || null,
          verified: verifiedOnly ? true : null,
          hasVariants: variantsOnly ? true : null,
          sort,
          limit: PRODUCT_PAGE_SIZE,
        });

        if (!isActive || latestRequestKeyRef.current !== requestKey) return;

        setItems(payload.items || []);
        setFilters(payload.filters || EMPTY_FILTERS);
        setNextCursor(payload.nextCursor || null);
        setHasMore(Boolean(payload.hasMore));
      } catch (fetchError) {
        if (!isActive || latestRequestKeyRef.current !== requestKey) return;
        setError("We couldn't load products right now.");
      } finally {
        if (isActive && latestRequestKeyRef.current === requestKey) {
          setIsInitialLoading(false);
        }
      }
    };

    loadProducts();

    return () => {
      isActive = false;
    };
  }, [
    deferredQuery,
    selectedCategoryId,
    selectedSupplierId,
    verifiedOnly,
    variantsOnly,
    sort,
    reloadTick,
  ]);

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMore || !nextCursor) return;

    const requestKey = latestRequestKeyRef.current;
    setIsLoadingMore(true);
    setError(null);

    try {
      const payload = await fetchWelcomeProducts({
        query: deferredQuery,
        categoryId: selectedCategoryId || null,
        sellerProfileId: selectedSupplierId || null,
        verified: verifiedOnly ? true : null,
        hasVariants: variantsOnly ? true : null,
        sort,
        cursor: nextCursor,
        limit: PRODUCT_PAGE_SIZE,
      });

      if (latestRequestKeyRef.current !== requestKey) return;

      setItems((currentItems) => {
        const existingIds = new Set(currentItems.map((item) => item.id));
        const appendedItems = (payload.items || []).filter(
          (item) => !existingIds.has(item.id)
        );
        return [...currentItems, ...appendedItems];
      });
      setNextCursor(payload.nextCursor || null);
      setHasMore(Boolean(payload.hasMore));
      setFilters(payload.filters || EMPTY_FILTERS);
    } catch (fetchError) {
      if (latestRequestKeyRef.current !== requestKey) return;
      setError("We couldn't load more products right now.");
    } finally {
      if (latestRequestKeyRef.current === requestKey) {
        setIsLoadingMore(false);
      }
    }
  };

  const selectedCategory =
    filters.categories.find((category) => category.id === selectedCategoryId) ||
    filters.rootCategories.find((category) => category.id === selectedCategoryId) ||
    null;
  const selectedSupplier =
    filters.suppliers.find((supplier) => supplier.id === selectedSupplierId) || null;
  const hasActiveFilters =
    Boolean(deferredQuery) ||
    Boolean(selectedCategoryId) ||
    Boolean(selectedSupplierId) ||
    verifiedOnly ||
    variantsOnly;

  const resetFilters = () => {
    setSearchInput("");
    setSelectedCategoryId("");
    setSelectedSupplierId("");
    setVerifiedOnly(false);
    setVariantsOnly(false);
    setSort("newest");
  };

  return (
    <div className="w-full">
      <section className="border-b border-border/70 bg-background">
        <div className="container mx-auto px-4 py-5 sm:px-6">
          <div className="card-surface overflow-hidden p-4 sm:p-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_220px]">
              <div className="space-y-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search products by name or code"
                    className="h-11 rounded-lg border-border bg-muted/50 pl-11 text-sm shadow-sm focus-visible:bg-background"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                  <span>
                    {isInitialLoading
                      ? "Loading products..."
                      : `${formatCompactCount(items.length)} products loaded`}
                  </span>
                  {deferredQuery ? (
                    <>
                      <span className="h-1 w-1 rounded-full bg-border" />
                      <span>Search: {deferredQuery}</span>
                    </>
                  ) : null}
                  {hasActiveFilters ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={resetFilters}
                      className="h-6 rounded px-2 text-muted-foreground hover:text-foreground"
                    >
                      Reset all
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                  Sort by
                </p>
                <Select value={sort} onValueChange={(value) => setSort(value as WelcomeProductSort)}>
                  <SelectTrigger className="h-11 rounded-lg bg-muted/40">
                    <SelectValue placeholder="Sort products" />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Popular product sections
              </div>
              <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
                <Button
                  type="button"
                  variant={!selectedCategoryId ? "default" : "outline"}
                  onClick={() => setSelectedCategoryId("")}
                  className="rounded-full whitespace-nowrap"
                >
                  All products
                </Button>
                {filters.rootCategories.map((category) => (
                  <Button
                    key={category.id}
                    type="button"
                    variant={
                      selectedCategoryId === category.id ? "default" : "outline"
                    }
                    onClick={() =>
                      setSelectedCategoryId((currentValue) =>
                        currentValue === category.id ? "" : category.id
                      )
                    }
                    className="rounded-full whitespace-nowrap"
                  >
                    {category.name}
                    <span className="ml-2 rounded-full bg-background/70 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      {formatCompactCount(category.productCount)}
                    </span>
                  </Button>
                ))}
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                  Category
                </p>
                <Select
                  value={selectedCategoryId || "all"}
                  onValueChange={(value) =>
                    setSelectedCategoryId(value === "all" ? "" : value)
                  }
                >
                  <SelectTrigger className="h-10 rounded-lg bg-muted/40">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value="all">All categories</SelectItem>
                    {filters.categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.displayName} ({formatCompactCount(category.productCount)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                  Supplier
                </p>
                <Select
                  value={selectedSupplierId || "all"}
                  onValueChange={(value) =>
                    setSelectedSupplierId(value === "all" ? "" : value)
                  }
                >
                  <SelectTrigger className="h-10 rounded-lg bg-muted/40">
                    <SelectValue placeholder="All suppliers" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value="all">All suppliers</SelectItem>
                    {filters.suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name} ({formatCompactCount(supplier.productCount)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                  Supplier status
                </p>
                <Button
                  type="button"
                  variant={verifiedOnly ? "default" : "outline"}
                  onClick={() => setVerifiedOnly((currentValue) => !currentValue)}
                  className="h-10 w-full justify-start rounded-lg"
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Verified suppliers only
                </Button>
              </div>

              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                  Product format
                </p>
                <Button
                  type="button"
                  variant={variantsOnly ? "default" : "outline"}
                  onClick={() => setVariantsOnly((currentValue) => !currentValue)}
                  className="h-10 w-full justify-start rounded-lg"
                >
                  <Layers3 className="mr-2 h-4 w-4" />
                  Variant products only
                </Button>
              </div>
            </div>

            {hasActiveFilters ? (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {deferredQuery ? (
                  <Badge variant="secondary">Search: {deferredQuery}</Badge>
                ) : null}
                {selectedCategory ? (
                  <Badge variant="secondary">
                    Category: {selectedCategory.name}
                  </Badge>
                ) : null}
                {selectedSupplier ? (
                  <Badge variant="secondary">
                    Supplier: {selectedSupplier.name}
                  </Badge>
                ) : null}
                {verifiedOnly ? (
                  <Badge variant="secondary">Verified only</Badge>
                ) : null}
                {variantsOnly ? (
                  <Badge variant="secondary">Variants only</Badge>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={resetFilters}
                  className="h-7 gap-1 rounded-full px-3 text-muted-foreground hover:text-foreground"
                >
                  <FilterX className="h-3.5 w-3.5" />
                  Clear filters
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 py-5 sm:px-6">
          <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                Source products at scale
              </h1>
              <p className="text-sm text-muted-foreground">
                Server-side filters and cursor pagination keep the catalog fast
                even as it grows.
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              Showing live catalog results from verified marketplace listings.
            </p>
          </div>

          {error ? (
            <div className="card-surface mb-4 border-destructive/20 bg-destructive/5 p-4">
              <div className="flex flex-col gap-4 text-sm sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-semibold text-foreground">
                    Products are unavailable right now
                  </h2>
                  <p className="mt-1 text-muted-foreground">{error}</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setReloadTick((currentValue) => currentValue + 1)}
                  className="text-foreground hover:bg-background hover:text-foreground"
                >
                  Try again
                </Button>
              </div>
            </div>
          ) : null}

          {isInitialLoading ? (
            <ProductGridSkeleton />
          ) : items.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 min-[520px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {items.map((item) => (
                <ProductCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="card-surface px-6 py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <PackageSearch className="h-5 w-5" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-foreground">
                No products matched these filters
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Try a broader search, choose a different category, or clear the
                current filters to keep browsing.
              </p>
              {hasActiveFilters ? (
                <div className="mt-5">
                  <Button type="button" variant="outline" onClick={resetFilters}>
                    Clear filters
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          <div className="mt-6 flex flex-col items-center gap-3">
            {hasMore ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="h-10 gap-2 text-foreground hover:border-slate-300 hover:bg-slate-100 hover:text-foreground dark:hover:border-slate-700 dark:hover:bg-slate-800"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading more products
                  </>
                ) : (
                  <>
                    Load more products
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            ) : !isInitialLoading && items.length > 0 ? (
              <p className="text-sm text-muted-foreground">
                You&apos;ve reached the end of the available products.
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}

export default function WelcomeProductsPage() {
  return (
    <Suspense
      fallback={
        <div className="w-full">
          <section className="border-b border-border/60 bg-background">
            <div className="container mx-auto px-4 py-4 sm:px-6 sm:py-5">
              <div className="card-surface p-3 sm:p-4">
                <div className="h-11 rounded-lg bg-muted/70 animate-pulse" />
                <div className="mt-2 h-4 w-40 rounded bg-muted animate-pulse" />
              </div>
            </div>
          </section>

          <section className="bg-slate-50 dark:bg-slate-900">
            <div className="container mx-auto px-4 py-4 sm:px-6 sm:py-5">
              <ProductGridSkeleton />
            </div>
          </section>
        </div>
      }
    >
      <WelcomeProductsPageContent />
    </Suspense>
  );
}